'use client';

import { type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { FilterSettingsMobileFilterDialogButtonFragmentFragmentDoc } from '@packages/gql/generated/shopping/FilterSettingsMobileFilterDialogButtonFragmentFragmentDoc';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import { FilterSettingsMobileButton } from '../FilterSettingsMobileButton/FilterSettingsMobileButton';
import { DynamicFilterSettingsMobilePopovers } from '../FilterSettingsMobile/FilterSettingsMobilePopovers.dynamic';
import { useNestedFilterDialogs } from '../FilterSettingsMobile/useNestedFilterDialogs';
import type { SelectedFilterValue } from '../../types';

/* GraphQL */ `
  fragment FilterSettingsMobileFilterDialogButtonFragment on SearchFilterableAndSortableResult {
    id
    ...FilterSettingsMobilePopoversFragment
  }
`;

export type FilterSettingsMobileFilterDialogButtonProps = {
  maskedData: FragmentType<typeof FilterSettingsMobileFilterDialogButtonFragmentFragmentDoc>;
  /** The list of currently selected filter values */
  selectedFilterValues: SelectedFilterValue[];
  /** Callback fired when a user submits the selection of one or more filter values */
  onSubmit?: (filterValues: SelectedFilterValue[]) => void;
};

const messages = defineMessages({
  filterButton: {
    id: 'filters.selection.mobile.filterButton.text',
    defaultMessage: 'Filtern / Sortieren',
  },
});

export const FilterSettingsMobileFilterDialogButton: FC<
  FilterSettingsMobileFilterDialogButtonProps
> = ({ maskedData, selectedFilterValues: initiallySelectedFilterValues, onSubmit }) => {
  const data = unmask<typeof FilterSettingsMobileFilterDialogButtonFragmentFragmentDoc>(maskedData);

  const { formatMessage } = useIntl();

  const {
    selectedFilterId,
    activeFilterIds,
    pendingFilterValues,
    hasFilterChanges,
    toggleFilterValue,
    setFilterRange,
    dialogControls,
  } = useNestedFilterDialogs({
    initiallySelectedFilterValues,
  });

  return (
    <>
      <FilterSettingsMobileButton onClick={dialogControls.filterDialog.open}>
        {formatMessage(messages.filterButton)}
      </FilterSettingsMobileButton>
      <DynamicFilterSettingsMobilePopovers
        requestCacheKey={data.id}
        maskedData={data}
        selectedFilterId={selectedFilterId}
        activeFilterIds={activeFilterIds}
        pendingFilterValues={pendingFilterValues}
        hasFilterChanges={hasFilterChanges}
        dialogControls={dialogControls}
        onToggle={toggleFilterValue}
        onSetRange={setFilterRange}
        onSubmit={onSubmit}
      />
    </>
  );
};
